import {
  ADMIN_JWT_TOKEN,
  ADMIN_SELECTED_LANGUAGE,
  API_URL,
  ERROR_MESSAGE,
} from '../constants/appConfig';
import { camelCase, download, getCurrentTimeZone, standardDisplayDateFormat } from './commonUtil';
import { getLocalStorage } from './storageUtil';
import Toast from '../components/shared/Toast';

import axios from 'axios';

export const downloadCSV = async ({ url, method = 'get', formData, fileName }) => {
  try {
    const response = await axios({
      url: `${API_URL}/${url}`,
      method: method,
      data: formData,
      headers: {
        'X-XSRF-TOKEN': getLocalStorage(ADMIN_JWT_TOKEN),
        'X-TIME-ZONE': getCurrentTimeZone(),
        'client-device': 'web',
        lang: getLocalStorage(ADMIN_SELECTED_LANGUAGE),
        'Content-Type': 'application/json',
      },
      responseType: 'blob',
    });

    if (response.status === 200) {
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      download(blob, fileName, 'csv');
      Toast('Download successful', 'SUCCESS');
      return response;
    }
  } catch (error) {
    Toast(ERROR_MESSAGE.message, 'ERROR');
    throw error;
  }
};

export const getCSVData = async (formData, module, moduleStates = {}, reportFileName) => {
  let csvFileName = {};
  const handleDownload = ({ responseData, fileName }) => {
    let blob = new Blob([responseData.data], {
      type: 'text/csv;charset=utf-8;',
    });
    download(blob, fileName, 'csv');
  };

  switch (module) {
    case 'sample-download':
      await axios
        .create({
          baseURL: `${API_URL}`,
          headers: {
            'X-XSRF-TOKEN': getLocalStorage(ADMIN_JWT_TOKEN),
            'X-TIME-ZONE': getCurrentTimeZone(),
            'client-device': 'web',
            lang: getLocalStorage(ADMIN_SELECTED_LANGUAGE),
          },
        })
        .get(`/atom-manager/v1/clients/${formData?.dataType}/upload/sample`)
        .then(response => {
          handleDownload({
            responseData: response,
            fileName: `${formData?.dataType}CSVSampleData`,
          });
        })
        .catch(e => console.error(e));
      break;

    case 'appointment-download':
      await axios
        .create({
          baseURL: `${API_URL}`,
          headers: {
            'X-XSRF-TOKEN': getLocalStorage(ADMIN_JWT_TOKEN),
            'X-TIME-ZONE': getCurrentTimeZone(),
            'client-device': 'web',
            lang: getLocalStorage(ADMIN_SELECTED_LANGUAGE),
          },
        })
        .post(`/atom-manager/v1/appointments/report/csv`, formData)
        .then(response => {
          handleDownload({
            responseData: response,
            fileName: `${standardDisplayDateFormat(
              formData?.fromDate
            )}-to-${standardDisplayDateFormat(formData?.toDate)}_Appointment`,
          });
          moduleStates?.setLoader(false);
          moduleStates?.setError({});
        })
        .catch(error => {
          error.response
            ? moduleStates?.setError({ message: error?.response?.data?.message })
            : moduleStates.setError({ message: ERROR_MESSAGE?.message });
          moduleStates?.setLoader(false);
        });
      break;
    // downloading csv files for invoices search in invoice page in admin module
    case 'invoices-search-download':
      await axios
        .create({
          baseURL: `${API_URL}`,
          headers: {
            'X-XSRF-TOKEN': getLocalStorage(ADMIN_JWT_TOKEN),
            'X-TIME-ZONE': getCurrentTimeZone(),
            'client-device': 'web',
            lang: getLocalStorage(ADMIN_SELECTED_LANGUAGE),
          },
        })
        .post(`/atom-manager/v1/invoices/search/download`, {
          from: formData?.fromDate,
          to: formData?.toDate,
        })
        .then(response => {
          handleDownload({
            responseData: response,
            fileName: `${standardDisplayDateFormat(
              formData?.fromDate
            )}-to-${standardDisplayDateFormat(formData?.toDate)}_Invoice`,
          });
          moduleStates?.setLoader(false);
          moduleStates?.setError({});
        })
        .catch(error => {
          error.response
            ? moduleStates?.setError({ message: error?.response?.data?.message })
            : moduleStates?.setError({ message: ERROR_MESSAGE?.message });
          moduleStates?.setLoader(false);
        });
      break;

    case 'clients':
      csvFileName = {
        name: `${camelCase(formData?.dataType)}_Client`,
        downloadStatus: false,
      };

      moduleStates.fileName.push(csvFileName);
      await axios
        .create({
          baseURL: `${API_URL}`,
          headers: {
            'X-XSRF-TOKEN': getLocalStorage(ADMIN_JWT_TOKEN),
            'X-TIME-ZONE': getCurrentTimeZone(),
            'client-device': 'web',
            lang: getLocalStorage(ADMIN_SELECTED_LANGUAGE),
          },
        })
        .post(`/atom-manager/v1/clients/report/${formData?.dataType}`, moduleStates?.formData)
        .then(response => {
          handleDownload({
            responseData: response,
            fileName: moduleStates?.reportCsv
              ? `${camelCase(formData?.dataType)} Return Report`
              : `${camelCase(formData?.dataType)}_Client`,
          });
          csvFileName.downloadStatus = true;
          moduleStates?.setLoader(false);
          moduleStates?.setError({});
        })
        .catch(error => {
          error.response
            ? moduleStates?.setError({ message: error?.response?.data?.message })
            : moduleStates.setError({ message: ERROR_MESSAGE?.message });
          csvFileName.downloadStatus = false;
          moduleStates?.setLoader(false);
        });

      break;

    case 'invoices':
      csvFileName = {
        name: `Invoice Report`,
        downloadStatus: false,
      };

      moduleStates.fileName.push(csvFileName);
      await axios
        .create({
          baseURL: `${API_URL}`,
          headers: {
            'X-XSRF-TOKEN': getLocalStorage(ADMIN_JWT_TOKEN),
            'X-TIME-ZONE': getCurrentTimeZone(),
            'client-device': 'web',
            lang: getLocalStorage(ADMIN_SELECTED_LANGUAGE),
          },
        })
        .post(`/atom-manager/v1/report/invoice`, formData)
        .then(response => {
          handleDownload({
            responseData: response,
            fileName: `Invoice Report`,
          });
          csvFileName.downloadStatus = true;
          moduleStates?.setLoader(false);
          moduleStates?.setError({});
        })
        .catch(error => {
          error.response
            ? moduleStates?.setError({ message: error?.response?.data?.message })
            : moduleStates.setError({ message: ERROR_MESSAGE?.message });
          csvFileName.downloadStatus = false;
          moduleStates?.setLoader(false);
        });

      break;

    case 'signaturePending':
      csvFileName = {
        name:
          formData?.dataType === ''
            ? `Signature Pending Report`
            : `${camelCase(formData?.dataType)} Signature Pending Report`,
        downloadStatus: false,
      };

      moduleStates.fileName.push(csvFileName);
      await axios
        .create({
          baseURL: `${API_URL}`,
          headers: {
            'X-XSRF-TOKEN': getLocalStorage(ADMIN_JWT_TOKEN),
            'X-TIME-ZONE': getCurrentTimeZone(),
            'client-device': 'web',
            lang: getLocalStorage(ADMIN_SELECTED_LANGUAGE),
          },
        })
        .post(`/atom-manager/v1/clients/tax-file/signature-pending/report`, moduleStates?.formData)
        .then(response => {
          if (response?.status === 200) {
            handleDownload({
              responseData: response,
              fileName:
                formData?.dataType === ''
                  ? `Signature Pending Report`
                  : `${camelCase(formData?.dataType)} Signature Pending Report`,
            });
            csvFileName.downloadStatus = true;
            moduleStates?.setLoader(false);
            moduleStates?.setError({});
          }
        })
        .catch(error => {
          error.response
            ? moduleStates?.setError({ message: error?.response?.data?.message })
            : moduleStates.setError({ message: ERROR_MESSAGE?.message });
          csvFileName.downloadStatus = false;
          moduleStates?.setLoader(false);
        });

      break;

    case 'tax-filling':
      csvFileName = {
        name: `${camelCase(formData?.dataType)}_TaxFiling`,
        downloadStatus: false,
      };

      moduleStates.fileName.push(csvFileName);
      await axios
        .create({
          baseURL: `${API_URL}`,
          headers: {
            'X-XSRF-TOKEN': getLocalStorage(ADMIN_JWT_TOKEN),
            'X-TIME-ZONE': getCurrentTimeZone(),
            'client-device': 'web',
            lang: getLocalStorage(ADMIN_SELECTED_LANGUAGE),
          },
        })
        .post(
          `/atom-manager/v1/clients/${formData?.dataType}/tax-file/e-file/report`,
          moduleStates?.formData
        )
        .then(response => {
          handleDownload({
            responseData: response,
            fileName: `${camelCase(formData?.dataType)}_TaxFiling`,
          });
          csvFileName.downloadStatus = true;
          moduleStates?.setLoader(false);
          moduleStates?.setError({});
        })
        .catch(error => {
          error.response
            ? moduleStates?.setError({ message: error?.response?.data?.message })
            : moduleStates.setError({ message: ERROR_MESSAGE?.message });
          csvFileName.downloadStatus = false;

          moduleStates?.setLoader(false);
        });
      break;

    case 'status':
      csvFileName = {
        name: `${camelCase(formData?.dataType)}_Status`,
        downloadStatus: false,
      };

      moduleStates.fileName.push(csvFileName);
      await axios
        .create({
          baseURL: `${API_URL}`,
          headers: {
            'X-XSRF-TOKEN': getLocalStorage(ADMIN_JWT_TOKEN),
            'X-TIME-ZONE': getCurrentTimeZone(),
            'client-device': 'web',
            lang: getLocalStorage(ADMIN_SELECTED_LANGUAGE),
          },
        })
        .post(
          `/atom-manager/v1/clients/status/report/${formData?.dataType}`,
          moduleStates?.formData
        )
        .then(response => {
          handleDownload({
            responseData: response,
            fileName: `${camelCase(formData?.dataType)}_Status`,
          });
          csvFileName.downloadStatus = true;
          moduleStates?.setLoader(false);
          moduleStates?.setError({});
        })
        .catch(error => {
          error.response
            ? moduleStates?.setError({ message: error?.response?.data?.message })
            : moduleStates.setError({ message: ERROR_MESSAGE?.message });
          csvFileName.downloadStatus = false;
          moduleStates?.setLoader(false);
        });
      break;

    case 'report':
      csvFileName = {
        name: reportFileName,
        downloadStatus: false,
      };

      moduleStates.fileName.push(csvFileName);

    case 'tasks':
      csvFileName = {
        name: `${camelCase(formData?.dataType)}_Tasks`,
        downloadStatus: false,
      };

      moduleStates?.fileName?.push(csvFileName);

      await axios
        .create({
          baseURL: `${API_URL}`,
          headers: {
            'X-XSRF-TOKEN': getLocalStorage(ADMIN_JWT_TOKEN),
            'X-TIME-ZONE': getCurrentTimeZone(),
            'client-device': 'web',
            lang: getLocalStorage(ADMIN_SELECTED_LANGUAGE),
          },
        })
        .post(
          `/atom-manager/v1/new/tasks/search/${formData?.dataType}/download`,
          moduleStates?.formData
        )
        .then(response => {
          handleDownload({
            responseData: response,
            fileName: `${camelCase(formData?.dataType)}_Tasks`,
          });

          csvFileName.downloadStatus = true;
          moduleStates?.setLoader(false);
          moduleStates?.setError({});
        })
        .catch(error => {
          if (error.response && error.response.data) {
            moduleStates?.setError({ message: 'Download failed' });
          } else {
            moduleStates?.setError({ message: ERROR_MESSAGE?.message });
          }

          csvFileName.downloadStatus = false;
          moduleStates?.setLoader(false);
        });

      break;
  }
};
